import { useMemo, useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { buildMemo, clipKindLabel, clipSourceLabel } from '../domain/notepad';

// Memo composer: pick which clips to include, preview the assembled memo, then
// copy (rich text + markdown), download .md, or print / save as PDF. Pure
// client-side — clip content is already in state; filenames resolve from
// AppContext.documents.

const PRINT_CSS = `
  body { font: 14px/1.5 -apple-system, Segoe UI, Roboto, sans-serif; color: #1a1a1a; max-width: 720px; margin: 32px auto; padding: 0 16px; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  .memo-sub { color: #666; margin: 0 0 20px; }
  h2 { font-size: 15px; margin: 22px 0 6px; }
  blockquote { margin: 6px 0; padding: 6px 12px; border-left: 3px solid #ccc; color: #333; font-style: italic; }
  .memo-note { margin: 6px 0; }
  .memo-attr { color: #888; font-size: 12px; margin: 4px 0 0; }
`;

export default function NotepadExportModal({ clips, notebookName, onClose }) {
  const { caseData, documents } = useApp();
  const documentsById = useMemo(() => {
    const m = {};
    (documents || []).forEach(d => { m[d.id] = d; });
    return m;
  }, [documents]);

  const [selected, setSelected] = useState(() => new Set(clips.map(c => c.id)));
  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const allOn = selected.size === clips.length && clips.length > 0;
  const setAll = (on) => setSelected(on ? new Set(clips.map(c => c.id)) : new Set());

  const included = clips.filter(c => selected.has(c.id));
  const { markdown, html } = useMemo(
    () => buildMemo(included, {
      caseName: caseData?.name,
      notebookName,
      dateStr: new Date().toLocaleDateString(),
      documentsById,
    }),
    [included, caseData?.name, notebookName, documentsById]
  );

  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.write([new window.ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([markdown], { type: 'text/plain' }),
      })]);
    } catch {
      try { await navigator.clipboard.writeText(markdown); } catch { /* ignore */ }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const download = () => {
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const base = `${caseData?.name || 'case'}${notebookName ? `-${notebookName}` : '-notes'}`;
    const safeName = base.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    a.href = url; a.download = `${safeName}.md`; a.click();
    URL.revokeObjectURL(url);
  };

  const printMemo = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>${(caseData?.name || 'Case')} — ${notebookName || 'Notes'}</title><style>${PRINT_CSS}</style></head><body>${html}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 250);
  };

  return (
    <div className="np-export-overlay" onClick={onClose}>
      <div className="np-export-modal" onClick={e => e.stopPropagation()}>
        <div className="np-export-head">
          <h3>Export memo</h3>
          <button className="np-icon-btn" onClick={onClose} title="Close">✕</button>
        </div>

        <div className="np-export-body">
          <div className="np-export-pick">
            <label className="np-export-all">
              <input type="checkbox" checked={allOn} onChange={e => setAll(e.target.checked)} />
              {' '}Include all ({clips.length})
            </label>
            {clips.map(c => (
              <label key={c.id} className="np-export-row">
                <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
                <span className="np-export-row-label">
                  <b>{clipKindLabel(c.kind)}</b> — {clipSourceLabel(c, documentsById)}
                  {(c.quote || c.note) && (
                    <span className="np-export-row-snip">
                      {' '}· {(c.note || c.quote).slice(0, 60)}…
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>

          <div className="np-export-preview">
            {included.length === 0
              ? <div className="np-empty">Select at least one clip.</div>
              : <div dangerouslySetInnerHTML={{ __html: html }} />}
          </div>
        </div>

        <div className="np-export-foot">
          <span className="np-export-count">{included.length} of {clips.length} selected</span>
          <span style={{ flex: 1 }} />
          <button className="btn" onClick={download} disabled={!included.length}>Download .md</button>
          <button className="btn" onClick={printMemo} disabled={!included.length}>Print / PDF</button>
          <button className="np-add-btn" onClick={copy} disabled={!included.length}>
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  );
}
